import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import { components as selectComponents } from "react-select";
import moment from "moment";
import { getDashboardElements, getOperationalDashboardSummary } from "../../api/dashboardsAPI";
import { getHierarchyStructures } from "../../api/hierarchy-structureAPI";
import HierarchySelect from "../common/HierarchySelect";
import { getEventTypeById } from "../../api/event-typeAPI";

const formatDateForQuery = (date) => date.toISOString().slice(0, 10);

const buildWeekOptionsForYear = (year) => {
  const options = [];
  const cutoff = new Date(year + 1, 0, 7);
  let current = new Date(year, 0, 1);
  const firstDayOffset = (current.getDay() + 6) % 7;
  current.setDate(current.getDate() - firstDayOffset);
  let weekNumber = 1;

  while (current < cutoff && options.length < 60) {
    const start = new Date(current);
    const end = new Date(current);
    end.setDate(start.getDate() + 6);
    options.push({
      label: `Week ${weekNumber} (${formatDateForQuery(start)} – ${formatDateForQuery(end)})`,
      start: formatDateForQuery(start),
      end: formatDateForQuery(end),
      weekNumber,
    });
    current.setDate(current.getDate() + 7);
    weekNumber += 1;
  }
  return options;
};

const buildMonthOptionsForYear = (year) => {
  const options = [];
  for (let monthIndex = 0; monthIndex < 12; monthIndex += 1) {
    const start = new Date(year, monthIndex, 1);
    const end = new Date(year, monthIndex + 1, 0);
    const label = `${start.toLocaleString("default", { month: "long" })} ${year}`;
    options.push({
      label,
      start: formatDateForQuery(start),
      end: formatDateForQuery(end),
      monthIndex,
    });
  }
  return options;
};

const displayValue = (value, fallback = "â€”") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }
  return String(value);
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const DATE_FILTER_OPTIONS = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "custom", label: "Custom" },
];

const DASHBOARD_METRIC_FILTERS = {
  due_range: "due_range",
  overdue: "overdue",
  completed: "completed",
};

const DATE_FILTER_OPTIONS_DAY_PREDEFINED = [
  { value: 7, label: "Next 7 Days" },
  { value: 14, label: "Next 14 Days" },
  { value: 30, label: "Next 30 Days" },
  { value: 60, label: "Next 60 Days" },
  { value: 90, label: "Next 90 Days" },
  { value: 180, label: "Next 180 Days" },
];

const MetricRow = ({ label, value, fallback = "—", to }) => (
  <div className="d-flex justify-content-between align-items-center gap-2 mb-2 metric-row">
    <div className="flex-grow-1">
      {to ? (
        <Link to={to} className="metric-link text-decoration-none fw-semibold fs-7">
          {label}
        </Link>
      ) : (
        <small className="text-secondary fs-7">{label}</small>
      )}
    </div>
    <span className="fw-semibold fs-5 text-dark">{displayValue(value, fallback)}</span>
  </div>
);

const LoadingDonut = ({ size = 10 }) => (
  <div className="d-flex align-items-center justify-content-center" style={{ minHeight: size }}>
    <style>{`
      @keyframes opDashboardSpin {
        0% { transform: rotate(0deg) scale(0.7); }
        50% { transform: rotate(180deg) scale(0.72); }
        100% { transform: rotate(360deg) scale(0.7); }
      }

      @keyframes opDashboardPulse {
        0%, 100% { opacity: 0.2; }
        50% { opacity: 0.3; }
      }
    `}</style>
    <div
      className="position-relative d-inline-flex align-items-center justify-content-center"
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        animation: "opDashboardSpin 1.8s linear infinite, opDashboardPulse 1.8s ease-in-out infinite",
        background:
          "conic-gradient(#c9cdd3 0deg 300deg, #e6e8ec 300deg 360deg)",
        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)",
      }}
      aria-label="Loading chart"
      role="status"
    >
      <div
        style={{
          width: Math.max(24, Math.round(size * 0.58)),
          height: Math.max(24, Math.round(size * 0.58)),
          borderRadius: "50%",
          background: "linear-gradient(180deg, #ffffff 0%, #f4f5f7 100%)",
          boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.05)",
        }}
      />
    </div>
  </div>
);

const buildDateRangeUrl = (base, startDate, endDate, dataFilterType, taskType, departmentIds) => {
  if (!base) return base;
  const params = new URLSearchParams();
  if (dataFilterType && dataFilterType != 'overdue') {
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);
  } else {
    params.delete("startDate");
    params.delete("endDate");
  }
  if (dataFilterType && dataFilterType == 'overdue') {
    params.set("overdue", true);
  } else {
    params.delete("overdue");
  }
  if (dataFilterType && dataFilterType == 'completed') {
    params.set("completed", true);
    params.set("taskType", taskType);
  } else {
    params.delete("completed");
    params.delete("taskType");
  }
  if (taskType == 'compliance' && dataFilterType == 'completed') {
    params.set("compliance", true)
  } else {
    params.delete("compliance")
  }

  if (Array.isArray(departmentIds) && departmentIds.length) {
    params.set("departmentIds", departmentIds.join(","));
  }
  if (!params.toString()) {
    return base;
  }
  const separator = base.includes("?") ? String.fromCharCode(38) : "?";
  return `${base}${separator}${params.toString()}`;
};

const buildDowntimeUrl = (base, departmentIds, eventType = undefined, isTotal = false) => {
  if (!base) return base;
  const params = new URLSearchParams();
  if (!eventType) {
    params.set("eventType", ["Breakdown", "Planned"].join(","));
  } else if (Array.isArray(eventType) && eventType.length > 0) {
    params.set("eventType", eventType.join(","));
  } else {
    params.set("eventType", [eventType].join(","));
  }
  params.set("tab", "In Progress");
  if (Array.isArray(departmentIds) && departmentIds.length) {
    params.set("departmentIds", departmentIds.join(","));
  }

  if (isTotal) {
    params.delete("eventType");
  }
  const separator = base.includes("?") ? String.fromCharCode(38) : "?";
  return `${base}${separator}${params.toString()}`;
};

const buildComponentShowMoreUrl = (base, startDate, endDate, departmentIds) => {
  if (!base) return base;
  const params = new URLSearchParams();
  if (startDate) params.set("startDate", startDate);
  if (endDate) params.set("endDate", endDate);
  if (Array.isArray(departmentIds) && departmentIds.length) {
    params.set("departmentIds", departmentIds.join(","));
  }
  const separator = base.includes("?") ? String.fromCharCode(38) : "?";
  return `${base}${separator}${params.toString()}`;
};

const DepartmentValueContainer = (props) => {
  const { children, getValue, hasValue, selectProps } = props;

  if (!selectProps.isMulti || !hasValue) {
    return <selectComponents.ValueContainer {...props}>{children}</selectComponents.ValueContainer>;
  }

  const values = getValue();
  if (!Array.isArray(values) || !values.length) {
    return <selectComponents.ValueContainer {...props}>{children}</selectComponents.ValueContainer>;
  }

  const label = values.length === 1 ? "1 Department" : `${values.length} Departments`;
  const input = Array.isArray(children) ? children[children.length - 1] : children;

  return (
    <selectComponents.ValueContainer {...props}>
      <div className="react-select__single-value">{label}</div>
      {input}
    </selectComponents.ValueContainer>
  );
};

const OperationalDashboardLayer = () => {
  const navigate = useNavigate();
  const getWeekRange = (date) => {
    const day = date.getDay();
    const diffToMonday = (day + 6) % 7;
    const monday = new Date(date);
    monday.setDate(date.getDate() - diffToMonday);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return [formatDateForQuery(monday), formatDateForQuery(sunday)];
  };

  const getMonthRange = (date) => {
    const first = new Date(date.getFullYear(), date.getMonth(), 1);
    const last = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return [formatDateForQuery(first), formatDateForQuery(last)];
  };

  const shiftDateByOneDay = (dateValue) => {
    const date = moment(dateValue, "YYYY-MM-DD");
    if (!date.isValid()) {
      return dateValue;
    }

    return date.add(1, "day").format("YYYY-MM-DD");
  };

  const today = useMemo(() => new Date(), []);
  const defaultRange = useMemo(() => getWeekRange(today), [today]);
  const [defaultStart, defaultEnd] = defaultRange;
  const yearOptions = useMemo(() => {
    const currentYear = today.getFullYear();
    return [currentYear, currentYear - 1];
  }, [today]);

  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [elements, setElements] = useState([]);
  const [elementsError, setElementsError] = useState("");
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState([]);
  const [departmentLoading, setDepartmentLoading] = useState(false);
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [dateFilterType, setDateFilterType] = useState("week");

  const [selectedDayPredefined, setSelectedDayPredefined] = useState(
    DATE_FILTER_OPTIONS_DAY_PREDEFINED[0]?.value ?? ""
  );

  const [rangeStartInput, setRangeStartInput] = useState(defaultStart);
  const [rangeEndInput, setRangeEndInput] = useState(defaultEnd);
  const [rangeDropdownOpen, setRangeDropdownOpen] = useState(false);
  const [selectedWeekYear, setSelectedWeekYear] = useState(yearOptions[0]);
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);
  const [selectedMonthYear, setSelectedMonthYear] = useState(yearOptions[0]);
  const [selectedMonthIndex, setSelectedMonthIndex] = useState(today.getMonth());
  const weekOptionsByYear = useMemo(() => {
    const map = {};
    yearOptions.forEach((year) => {
      map[year] = buildWeekOptionsForYear(year);
    });
    return map;
  }, [yearOptions]);
  const monthOptionsByYear = useMemo(() => {
    const map = {};
    yearOptions.forEach((year) => {
      map[year] = buildMonthOptionsForYear(year);
    });
    return map;
  }, [yearOptions]);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await getOperationalDashboardSummary({
        startDate,
        endDate,
        dateFilterType,
        departmentIds: selectedDepartmentIds,
      });
      setMetrics(payload);
    } catch (err) {
      console.error("Unable to load operational dashboard", err);
      setError("Unable to load operational metrics right now.");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, dateFilterType, selectedDepartmentIds]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const fetchElements = useCallback(async () => {
    setElementsError("");
    try {
      const data = await getDashboardElements();
      setElements(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Unable to load dashboard element metadata", err);
      setElementsError("Unable to load dashboard layout definitions.");
    }
  }, []);

  useEffect(() => {
    fetchElements();
  }, [fetchElements]);

  const fetchDepartments = useCallback(async () => {
    setDepartmentLoading(true);
    try {
      const data = await getHierarchyStructures();
      const mapped = Array.isArray(data)
        ? data
          .map((department) => ({
            value: department?.id,
            label: department?.name,
            parentId: department?.parentId ?? null,
          }))
          .filter(
            (department) =>
              department.value !== undefined &&
              department.value !== null &&
              Boolean(department.label),
          )
        : [];
      setDepartmentOptions(mapped);
    } catch (err) {
      console.error("Unable to load department options", err);
      setDepartmentOptions([]);
    } finally {
      setDepartmentLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  const toggleRangeDropdown = () => {
    setRangeDropdownOpen((prev) => !prev);
  };

  const applyRange = useCallback(
    (start, end, filterType) => {
      const normalizedFilterType = filterType ?? dateFilterType;
      setStartDate(start);
      setEndDate(end);
      setRangeStartInput(start);
      setRangeEndInput(end);
      setDateFilterType(normalizedFilterType);
    },
    [dateFilterType],
  );

  const handleDateFilterToggle = (filterType) => {
    const now = new Date();
    if (filterType === "day") {
      const formatted = formatDateForQuery(now);
      applyRange(formatted, formatted, "day");
      return;
    }
    const [start, end] =
      filterType === "month" ? getMonthRange(now) : getWeekRange(now);
    applyRange(
      filterType === "month" ? shiftDateByOneDay(start) : start,
      filterType === "month" ? shiftDateByOneDay(end) : end,
      filterType,
    );
    if (filterType === "week") {
      const targetYear = now.getFullYear();
      setSelectedWeekYear(targetYear);
      const options = weekOptionsByYear[targetYear] || [];
      const matchedIndex = options.findIndex((option) => option.start === start);
      setSelectedWeekIndex(matchedIndex >= 0 ? matchedIndex : 0);
    } else {
      const targetYear = now.getFullYear();
      setSelectedMonthYear(targetYear);
      const options = monthOptionsByYear[targetYear] || [];
      const matchedIndex = options.findIndex((option) => option.monthIndex === now.getMonth());
      setSelectedMonthIndex(matchedIndex >= 0 ? matchedIndex : 0);
    }
  };
  const handleWeekYearChange = (year) => {
    const numericYear = Number(year);
    setSelectedWeekYear(numericYear);
    const options = weekOptionsByYear[numericYear] || [];
    const normalizedIndex = 0;
    setSelectedWeekIndex(normalizedIndex);
    const selection = options[normalizedIndex];
    if (selection) {
      applyRange(selection.start, selection.end, "week");
    }
  };
  const handleWeekSelectionChange = (index) => {
    const numericIndex = Number(index);
    setSelectedWeekIndex(numericIndex);
    const options = weekOptionsByYear[selectedWeekYear] || [];
    const selection = options[numericIndex];
    if (selection) {
      applyRange(selection.start, selection.end, "week");
    }
  };
  const handleMonthYearChange = (year) => {
    const numericYear = Number(year);
    setSelectedMonthYear(numericYear);
    const options = monthOptionsByYear[numericYear] || [];
    const normalizedIndex = 0;
    setSelectedMonthIndex(normalizedIndex);
    const selection = options[normalizedIndex];
    if (selection) {
      applyRange(shiftDateByOneDay(selection.start), shiftDateByOneDay(selection.end), "month");
    }
  };
  const handleMonthSelectionChange = (index) => {
    const numericIndex = Number(index);
    setSelectedMonthIndex(numericIndex);
    const options = monthOptionsByYear[selectedMonthYear] || [];
    const selection = options[numericIndex];
    if (selection) {
      applyRange(shiftDateByOneDay(selection.start), shiftDateByOneDay(selection.end), "month");
    }
  };
  const handleApplyRange = (rangeType = "day") => {
    const start = rangeStartInput;
    const end = rangeEndInput;
    if (!start || !end) {
      return;
    }
    if (start > end) {
      applyRange(end, start, rangeType);
    } else {
      applyRange(start, end, rangeType);
    }
    setRangeDropdownOpen(false);
  };
  const downtimeData = useMemo(() => metrics?.downtime ?? {}, [metrics]);
  // const downtimeDataOthers = useMemo(() => metrics?.downtime ?? {}, [metrics]);
  const inspectionsData = useMemo(() => metrics?.inspections ?? {}, [metrics]);
  const servicesData = useMemo(() => metrics?.services ?? {}, [metrics]);
  const complianceData = useMemo(() => metrics?.compliance ?? {}, [metrics]);
  const assetOverviewData = useMemo(() => metrics?.assetOverview ?? {}, [metrics]);

  const downtimeBreakdown = useMemo(() => downtimeData.assetsDownByEventType?.filter((item) => item.eventType == "Breakdown" || item.eventType == "Planned") ?? [], [
    downtimeData,
  ]);
  const downtimeBreakdownOthers = useMemo(() => downtimeData.assetsDownByEventType?.filter((item) => item.eventType !== "Breakdown" && item.eventType !== "Planned") ?? [], [
    downtimeData,
  ]);

  // const totalAssetsDown = useMemo(() => {
  //   return downtimeBreakdown.reduce((sum, current) => sum + toNumber(current.eventCount), 0);
  // }, [downtimeBreakdown]);

  const totalAssetsDownOthers = useMemo(() => {
    return downtimeBreakdownOthers.reduce((sum, current) => sum + toNumber(current.eventCount), 0);
  }, [downtimeBreakdownOthers]);
  const downtimeChartPoints = useMemo(
    () =>
      downtimeBreakdown.map((row) => ({
        eventType: row?.eventType || "Other",
        count: toNumber(row?.eventCount),
        raw: row,
      })).concat(
        downtimeBreakdownOthers.map((row) => ({
          eventType: row?.eventType || "Other",
          count: toNumber(row?.eventCount),
          raw: row,
        })),
      ),
    [downtimeBreakdown, downtimeBreakdownOthers],
  );
  const downtimeChartColors = useMemo(() => {
    const palette = ["#3598db", "#f1c40f", "#2dcc70", "#9a59b5", "#f97316", "#14b8a6", "#6366f1"];
    const hashLabel = (label) =>
      String(label || "")
        .split("")
        .reduce((sum, char) => sum + char.charCodeAt(0), 0);

    return downtimeChartPoints.map((point) => {
      if (String(point.eventType || "").trim().toLowerCase() === "breakdown") {
        return "#e84c3d";
      }

      return palette[hashLabel(point.eventType) % palette.length];
    });
  }, [downtimeChartPoints]);
  const [selectedDowntimeSlice, setSelectedDowntimeSlice] = useState(null);


  const rangeDisplayLabel = useMemo(() => {
    const start = moment(startDate, "YYYY-MM-DD");
    const end = moment(endDate, "YYYY-MM-DD");

    if (!start.isValid() || !end.isValid()) {
      return "";
    }

    return `${start.format("MMM D")} - ${end.format("MMM D YYYY")}`;
  }, [dateFilterType, startDate, endDate]);

  const displayRangeStartInput = useMemo(() => {
    if (dateFilterType !== "month") {
      return rangeStartInput;
    }

    const start = moment(rangeStartInput, "YYYY-MM-DD");
    if (!start.isValid()) {
      return rangeStartInput;
    }

    if (dateFilterType === "month") {
      return start.format("MMM D");
    }
  }, [dateFilterType, rangeStartInput]);

  const displayRangeEndInput = useMemo(() => {
    if (dateFilterType !== "month") {
      return rangeEndInput;
    }

    const end = moment(rangeEndInput, "YYYY-MM-DD");
    if (!end.isValid()) {
      return end.format("MMM D YYYY");
    }

    if (dateFilterType === "month") {
      return end.format("MMM D YYYY");
    }
  }, [dateFilterType, rangeEndInput]);

  const dueMetricLabel = useMemo(() => "Due", []);
  const overdueMetricLabel = useMemo(() => "Overdue", []);
  const completedMetricLabel = useMemo(() => "Completed", []);

  const downtimeChartSeries = useMemo(
    () => downtimeChartPoints.map((row) => row.count),
    [downtimeChartPoints],
  );


  const elementMap = useMemo(() => {
    const map = new Map();
    elements.forEach((element) => {
      if (element?.name) {
        map.set(element.name, element);
      }
    });
    return map;
  }, [elements]);

  const resolveElement = (name, fallbackTitle, fallbackPath) => {
    const element = elementMap.get(name);
    return {
      title: element?.displayName ?? fallbackTitle,
      href: element?.redirectPagePath ?? fallbackPath,
    };
  };

  const downtimeElement = resolveElement(
    "dashboard-downtime",
    "Downtime",
    "/dashboards/downtime",
  );
  const inspectionsElement = resolveElement(
    "dashboard-inspections",
    "Inspections",
    "/inspections-management",
  );
  const servicesElement = resolveElement(
    "dashboard-services",
    "Services",
    "/service-management",
  );
  const complianceElement = resolveElement(
    "dashboard-compliance",
    "Compliance",
    "/compliance-management",
  );
  const utilisationElement = resolveElement(
    "dashboard-utilization",
    "Utilisation (TBD)",
    "/dashboards/utilisation",
  );
  const assetOverviewElement = resolveElement(
    "dashboard-asset-overview",
    "Asset Overview",
    "/asset-list",
  );

  const WORK_ORDERS_BASE_ROUTE = '/work-orders'
  // const colors = ["#3598db", "#e84c3d", "#f1c40f", "#2dcc70", "#9a59b5"];
  const createPieChartConfig = useCallback(({
    due,
    overdue,
    completed,
    labels = ["Due", "Overdue", "Completed"],
    colors = ["#f1c40f", "#e84c3d", "#2dcc70"],
    drilldownUrls = [],
  }) => {
    const dueValue = toNumber(due);
    const overdueValue = toNumber(overdue);
    const completedValue = toNumber(completed);
    const series = [dueValue, overdueValue, completedValue];
    return {
      series,
      options: {
        chart: {
          type: "pie",
          toolbar: { show: false },
          events: {
            dataPointSelection: (_event, _chartContext, config) => {
              const targetUrl = drilldownUrls?.[config?.dataPointIndex];
              if (!targetUrl) {
                return;
              }

              window.setTimeout(() => {
                navigate(targetUrl);
              }, 0);
            },
          },
        },
        colors,
        labels,
        dataLabels: {
          enabled: true,
          formatter: (_value, opts) => {
            const seriesValue = series[opts.seriesIndex] ?? 0;
            return seriesValue ? displayValue(seriesValue, "0") : "";
          },
          style: {
            colors: ["#fff"],
            fontWeight: 700,
          },
          dropShadow: {
            enabled: true,
            top: 1,
            left: 1,
            blur: 2,
            opacity: 0.45,
          },
        },
        legend: { position: "bottom", horizontalAlign: "center", fontSize: "12px" },
        tooltip: {
          y: {
            formatter: (_value, opts) => displayValue(series[opts.seriesIndex], "0"),
          },
        },
        stroke: { width: 0 },
      },
    };
  }, [navigate]);

  const inspectionsDrilldownUrls = useMemo(
    () => [
      buildDateRangeUrl(
        inspectionsElement.href,
        startDate,
        endDate,
        DASHBOARD_METRIC_FILTERS.due_range,
        "inspection",
        selectedDepartmentIds,
      ),
      buildDateRangeUrl(
        inspectionsElement.href,
        startDate,
        endDate,
        DASHBOARD_METRIC_FILTERS.overdue,
        "inspection",
        selectedDepartmentIds,
      ),
      buildDateRangeUrl(
        inspectionsElement.href,
        startDate,
        endDate,
        DASHBOARD_METRIC_FILTERS.completed,
        "inspection",
        selectedDepartmentIds,
      ),
    ],
    [endDate, inspectionsElement.href, selectedDepartmentIds, startDate],
  );

  const servicesDrilldownUrls = useMemo(
    () => [
      buildDateRangeUrl(
        servicesElement.href,
        startDate,
        endDate,
        DASHBOARD_METRIC_FILTERS.due_range,
        "service",
        selectedDepartmentIds,
      ),
      buildDateRangeUrl(
        servicesElement.href,
        startDate,
        endDate,
        DASHBOARD_METRIC_FILTERS.overdue,
        "service",
        selectedDepartmentIds,
      ),
      buildDateRangeUrl(
        servicesElement.href,
        startDate,
        endDate,
        DASHBOARD_METRIC_FILTERS.completed,
        "service",
        selectedDepartmentIds,
      ),
    ],
    [endDate, selectedDepartmentIds, servicesElement.href, startDate],
  );

  const complianceDrilldownUrls = useMemo(
    () => [
      buildDateRangeUrl(
        complianceElement.href,
        startDate,
        endDate,
        DASHBOARD_METRIC_FILTERS.due_range,
        "compliance",
        selectedDepartmentIds,
      ),
      buildDateRangeUrl(
        complianceElement.href,
        startDate,
        endDate,
        DASHBOARD_METRIC_FILTERS.overdue,
        "compliance",
        selectedDepartmentIds,
      ),
      buildDateRangeUrl(
        complianceElement.href,
        startDate,
        endDate,
        DASHBOARD_METRIC_FILTERS.completed,
        "compliance",
        selectedDepartmentIds,
      ),
    ],
    [complianceElement.href, endDate, selectedDepartmentIds, startDate],
  );

  const inspectionsChart = useMemo(
    () =>
      createPieChartConfig({
        due: inspectionsData.inspectionsDueThisWeek,
        overdue: inspectionsData.inspectionsOverdue,
        completed: inspectionsData.inspectionsCompletedThisWeek,
        drilldownUrls: inspectionsDrilldownUrls,
      }),
    [
      createPieChartConfig,
      inspectionsDrilldownUrls,
      inspectionsData.inspectionsDueThisWeek,
      inspectionsData.inspectionsOverdue,
      inspectionsData.inspectionsCompletedThisWeek,
    ],
  );

  const servicesChart = useMemo(
    () =>
      createPieChartConfig({
        due: servicesData.servicesDueThisWeek,
        overdue: servicesData.servicesOverdue,
        completed: servicesData.servicesCompletedThisWeek,
        drilldownUrls: servicesDrilldownUrls,
      }),
    [
      createPieChartConfig,
      servicesDrilldownUrls,
      servicesData.servicesDueThisWeek,
      servicesData.servicesOverdue,
      servicesData.servicesCompletedThisWeek,
    ],
  );

  const complianceChart = useMemo(
    () =>
      createPieChartConfig({
        due: complianceData.complianceDueThisWeek,
        overdue: complianceData.complianceOverdue,
        completed: complianceData.complianceCompletedThisWeek,
        drilldownUrls: complianceDrilldownUrls,
      }),
    [
      createPieChartConfig,
      complianceDrilldownUrls,
      complianceData.complianceDueThisWeek,
      complianceData.complianceOverdue,
      complianceData.complianceCompletedThisWeek,
    ],
  );


const downtimeRedirectUrl = useMemo(
  () => buildDowntimeUrl(downtimeElement.href, selectedDepartmentIds, undefined, false),
  [downtimeElement.href, selectedDepartmentIds],
);

const downtimeChartOptions = useMemo(() => {
    return {
      chart: {
        type: "pie",
        toolbar: { show: false },
      events: {
        dataPointSelection: (_event, _chartContext, config) => {
          const index = config?.dataPointIndex;
          const selected = downtimeChartPoints[index];
          if (!selected) {
            return;
          }
          window.setTimeout(() => {
            navigate(buildDowntimeUrl(downtimeElement.href, selectedDepartmentIds, selected.eventType));
          }, 1);
        },
      },
    },
    labels: downtimeChartPoints.map((row) => row.eventType),
    colors: downtimeChartColors,
    dataLabels: {
      enabled: true,
      formatter: (val, opts) => {
        const value = downtimeChartSeries[opts.seriesIndex];
        return value ? displayValue(value) : "0";
      },
      dropShadow: { enabled: false },
    },
    legend: { position: "bottom", horizontalAlign: "center" },
    tooltip: {
      y: {
        formatter: (_val, opts) =>
          displayValue(downtimeChartSeries[opts.seriesIndex], "0"),
      },
    },
    stroke: { width: 0 },
  };
}, [downtimeChartPoints, downtimeChartColors, downtimeChartSeries, downtimeRedirectUrl, navigate]);

  const handleSetSelectedDayPredefined = (day) => {
    const today = moment();
    const endDate = moment().add(day, "days");

    setRangeStartInput(today.format("YYYY-MM-DD"));
    setRangeEndInput(endDate.format("YYYY-MM-DD"));
    setSelectedDayPredefined(day);
  };

  return (
    <div className="operational-dashboard-layer">
      <style>
        {`
          .operational-dashboard-layer .card {
            font-size: 1rem;
          }
          .operational-dashboard-layer h6 {
            font-size: 1.6rem;
          }
          .operational-dashboard-layer .text-secondary {
            font-size: 1.1rem;
          }
          .operational-dashboard-layer .badge {
            font-size: 0.95rem;
            min-height: 28px;
            padding: 0.4rem 0.75rem;
          }
          .operational-dashboard-layer .metric-row span.fw-semibold {
            font-size: 1.4rem;
          }
          .operational-dashboard-layer .metric-row small {
            font-size: 1rem;
          }
          .operational-dashboard-layer .metric-link {
            color: #0d6efd;
            transition: color 0.15s ease-in-out;
          }
          .operational-dashboard-layer .metric-link:hover {
            color: #66a3ff;
          }
          .operational-dashboard-layer .range-toggle {
            border: 1px solid #dee2e6;
            border-radius: 999px;
            background-color: #fff;
            overflow: hidden;
            box-shadow: none;
          }
          .operational-dashboard-layer .range-toggle button {
            border: none;
            border-radius: 0;
            padding: 0.35rem 1.25rem;
            font-size: 0.95rem;
          }
          .operational-dashboard-layer .range-toggle button.active {
            box-shadow: none;
          }
          .operational-dashboard-layer .range-dropdown-button {
            min-width: 220px;
            text-align: center;
          }
          .operational-dashboard-layer .asset-departments {
            list-style: none;
            margin: 0;
            padding-left: 1rem;
          }
          .operational-dashboard-layer .asset-departments li {
            font-size: 1rem;
          }
        `}
      </style>
      <div className="row gy-4 p-24">
        {error && (
          <div className="col-12">
            <div className="alert alert-danger mb-0" role="alert">
              {error}
            </div>
          </div>
        )}
        {elementsError && (
          <div className="col-12">
            <div className="alert alert-warning mb-0" role="status">
              {elementsError}
            </div>
          </div>
        )}
        <div className="col-12">
          <div className="d-flex align-items-center flex-wrap gap-4 pe-3">

            <div style={{ minWidth: "320px", maxWidth: "380px", flex: "1 1 320px" }}>
              <HierarchySelect
                isMulti
                options={departmentOptions}
                value={selectedDepartmentIds}
                onChange={setSelectedDepartmentIds}
                components={{ ValueContainer: DepartmentValueContainer }}
                isClearable
                isLoading={departmentLoading}
                isDisabled={loading || departmentLoading}
                placeholder={departmentLoading ? "Loading departments..." : "All accessible departments"}
              />
            </div>


            <div id="range-toggle-date-dilter" className="range-toggle d-flex">
              {DATE_FILTER_OPTIONS.map((option, index) => {
                const isActive = dateFilterType === option.value;
                const buttonClasses = [
                  "btn",
                  "btn-sm",
                  "text-capitalize",
                  "fw-semibold",
                  "px-3",
                  "py-1",
                  isActive ? "btn-primary text-white" : "btn-outline-primary",
                  index === 0 ? "rounded-start-pill" : "",
                  index === DATE_FILTER_OPTIONS.length - 1 ? "rounded-end-pill" : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <button
                    key={option.value}
                    disabled={loading}
                    type="button"
                    className={buttonClasses}
                    onClick={() => handleDateFilterToggle(option.value)}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>

            {rangeDisplayLabel && (
              <div className="text-secondary fs-7 fw-semibold ms-2">
                {rangeDisplayLabel}
              </div>
            )}

            <div className="d-flex flex-grow-1 justify-content-center range-controls ms-auto" style={{ minWidth: "300px", maxWidth: "300px" }}>

              <div className="dropdown range-controls ms-auto" style={{ position: "relative" }}>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary dropdown-toggle range-dropdown-button"
                  onClick={toggleRangeDropdown}
                  disabled={loading}
                  aria-expanded={rangeDropdownOpen}
                >
                  {loading ? "Loading..." : displayRangeStartInput + " – " + displayRangeEndInput}
                </button>
                <div
                  className={`dropdown-menu dropdown-menu-end p-3 ${rangeDropdownOpen ? "show" : ""}`}
                  style={{ minWidth: "340px", right: 0, left: "auto" }}
                >
                  {dateFilterType === "week" ? (
                    <>
                      <label className="form-label fs-7 text-secondary mb-1">Year</label>
                      <select
                        className="form-select form-select-sm dropdown mb-2"
                        value={selectedWeekYear}
                        onChange={(event) => handleWeekYearChange(event.target.value)}
                      >
                        {yearOptions.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                      <label className="form-label fs-7 text-secondary mb-1">Week</label>
                      <select
                        className="form-select form-select-sm mb-3 dropdown"
                        value={selectedWeekIndex}
                        onChange={(event) => handleWeekSelectionChange(event.target.value)}
                      >
                        {weekOptionsByYear[selectedWeekYear]?.map((option, index) => (
                          <option key={`${selectedWeekYear}-${index}`} value={index}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary w-100"
                        onClick={() => setRangeDropdownOpen(false)}
                      >
                        Apply week
                      </button>
                    </>
                  ) : dateFilterType === "month" ? (
                    <>
                      <label className="form-label fs-7 text-secondary mb-1">Year</label>
                      <select
                        className="form-select form-select-sm mb-2"
                        value={selectedMonthYear}
                        onChange={(event) => handleMonthYearChange(event.target.value)}
                      >
                        {yearOptions.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                      <label className="form-label fs-7 text-secondary mb-1">Month</label>
                      <select
                        className="form-select form-select-sm mb-3"
                        value={selectedMonthIndex}
                        onChange={(event) => handleMonthSelectionChange(event.target.value)}
                      >
                        {monthOptionsByYear[selectedMonthYear]?.map((option, index) => (
                          <option key={`${selectedMonthYear}-${option.monthIndex}`} value={index}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary w-100"
                        onClick={() => setRangeDropdownOpen(false)}
                      >
                        Apply month
                      </button>
                    </>
                  ) : dateFilterType === "day" ? (
                    <>
                      <select
                        className="form-select form-select-sm mb-3"
                        value={selectedDayPredefined}
                        onChange={(e) => { handleSetSelectedDayPredefined(Number(e.target.value)); handleApplyRange("day") }}
                      >
                        {DATE_FILTER_OPTIONS_DAY_PREDEFINED.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </>
                  ) : <>
                    <label className="form-label fs-7 text-secondary mb-1">Start date</label>
                    <input
                      type="date"
                      className="form-control form-control-sm mb-2"
                      value={rangeStartInput}
                      max={rangeEndInput}
                      onChange={(event) => setRangeStartInput(event.target.value)}
                    />
                    <label className="form-label fs-7 text-secondary mb-1">End date</label>
                    <input
                      type="date"
                      className="form-control form-control-sm mb-3"
                      value={rangeEndInput}
                      min={rangeStartInput}
                      onChange={(event) => setRangeEndInput(event.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-primary w-100"
                      onClick={() => handleApplyRange("custom")}
                    >
                      Apply range
                    </button>
                  </>}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 col-lg-4">
          <div className="card h-100 border-0 shadow-sm">
            <div className="card-body d-flex flex-column">
              <div>
                <h6 className="fw-semibold mb-1">{downtimeElement.title}</h6>
              </div>
              <div className="d-flex flex-wrap gap-3">
                <div className="flex-fill" style={{ minWidth: "180px" }}>
                  {loading ? (
                    <LoadingDonut size={200} />
                  ) : downtimeChartSeries.length ? (
                    <ReactApexChart
                      options={downtimeChartOptions}
                      series={downtimeChartSeries}
                      type="pie"
                      height={200}
                    />
                  ) : (
                    <div className="text-center text-secondary py-5 rounded-3 border border-dashed">
                      No breakdown data yet
                    </div>
                  )}
                </div>
              </div>
              {selectedDowntimeSlice && (
                <div className="alert alert-light border py-2 px-3 mb-3 mt-2">
                  <div className="fw-semibold">Selected slice</div>
                  <div className="text-secondary fs-7">
                    {selectedDowntimeSlice.eventType} {selectedDowntimeSlice.count}
                  </div>
                </div>
              )}
              <>
                {downtimeBreakdown.length > 0 &&
                  downtimeBreakdown.map((item) => (
                    <MetricRow key={`${item.eventType}-${item.eventCount}`}
                      to={buildDowntimeUrl(downtimeElement.href, selectedDepartmentIds, item.eventType)}
                      className="metric-link text-decoration-none fw-semibold fs-7" label={item.eventType} value={item.eventCount} fallback="0" />
                  ))}
              </>

              {/* <MetricRow label="Total assets down"
              to={buildDowntimeUrl(downtimeElement.href, selectedDepartmentIds, undefined, true)}
              value={totalAssetsDown} fallback="0"
              className="metric-link text-decoration-none fw-semibold fs-7"
            /> */}

              {/* <MetricRow
              label="Breakdown"
              to={buildDowntimeUrl(downtimeElement.href, selectedDepartmentIds, ["Breakdown"])}
              value={downtimeData.breakdowns}
              className="metric-link text-decoration-none fw-semibold fs-7"
              fallback="0"
            />
            <MetricRow
              label="Planned"
              to={buildDowntimeUrl(downtimeElement.href, selectedDepartmentIds, ["Planned"])}
              value={downtimeData.planned}
              className="metric-link text-decoration-none fw-semibold fs-7"
              fallback="0"
            /> */}

              <MetricRow
                label="Other (Accident / Opportune / Standby)"
                to={buildDowntimeUrl(downtimeElement.href, selectedDepartmentIds, ["Accident Damage", "Opportune", "Standby"])}
                value={totalAssetsDownOthers}
                className="metric-link text-decoration-none fw-semibold fs-7"
                fallback="0"
              />
              {/* <MetricRow label="Overdue (Past Estimated Up)" value={downtimeData.overdue} fallback="0" /> */}
              <div className="mt-auto text-end">
                <Link
                  to={downtimeRedirectUrl}
                  className="btn btn-sm btn-outline-secondary text-capitalize"
                >
                  show more...
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 col-lg-4">
          <div className="card h-100 border-0 shadow-sm">
            <div className="card-body d-flex flex-column gap-2">
              <h6 className="fw-semibold mb-0">{inspectionsElement.title}</h6>
              <div>
                {loading ? (
                  <LoadingDonut size={180} />
                ) : (
                  <ReactApexChart
                    options={inspectionsChart.options}
                    series={inspectionsChart.series}
                    type="pie"
                    height={180}
                  />
                )}
              </div>
              <MetricRow
                label={dueMetricLabel}
                value={inspectionsData.inspectionsDueThisWeek}
                fallback="0"
                to={buildDateRangeUrl(
                  inspectionsElement.href,
                  startDate,
                  endDate,
                  DASHBOARD_METRIC_FILTERS.due_range,
                  "inspection",
                  selectedDepartmentIds,
                )}
              />
              <MetricRow
                label={overdueMetricLabel}
                value={inspectionsData.inspectionsOverdue}
                fallback="0"
                to={buildDateRangeUrl(
                  inspectionsElement.href,
                  startDate,
                  endDate,
                  DASHBOARD_METRIC_FILTERS.overdue,
                  "inspection",
                  selectedDepartmentIds,
                )}
              />
              <MetricRow
                label={completedMetricLabel}
                value={inspectionsData.inspectionsCompletedThisWeek}
                fallback="0"
                to={buildDateRangeUrl(
                  WORK_ORDERS_BASE_ROUTE,
                  startDate,
                  endDate,
                  DASHBOARD_METRIC_FILTERS.completed,
                  "inspection",
                  selectedDepartmentIds,
                )}
              />
              <div className="mt-auto text-end">
                <Link
                  to={buildComponentShowMoreUrl(inspectionsElement.href, startDate, endDate, selectedDepartmentIds)}
                  className="btn btn-sm btn-outline-secondary text-capitalize"
                >
                  show more...
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 col-lg-4">
          <div className="card h-100 border-0 shadow-sm">
            <div className="card-body d-flex flex-column gap-2">
              <h6 className="fw-semibold mb-0">{servicesElement.title}</h6>
              <div>
                {loading ? (
                  <LoadingDonut size={180} />
                ) : (
                  <ReactApexChart
                    options={servicesChart.options}
                    series={servicesChart.series}
                    type="pie"
                    height={180}
                  />
                )}
              </div>
              <MetricRow
                label={dueMetricLabel}
                value={servicesData.servicesDueThisWeek}
                fallback="0"
                to={buildDateRangeUrl(
                  servicesElement.href,
                  startDate,
                  endDate,
                  DASHBOARD_METRIC_FILTERS.due_range,
                  "service",
                  selectedDepartmentIds,
                )}
              />
              <MetricRow
                label={overdueMetricLabel}
                value={servicesData.servicesOverdue}
                fallback="0"
                to={buildDateRangeUrl(
                  servicesElement.href,
                  startDate,
                  endDate,
                  DASHBOARD_METRIC_FILTERS.overdue,
                  "service",
                  selectedDepartmentIds,
                )}
              />
              <MetricRow
                label={completedMetricLabel}
                value={servicesData.servicesCompletedThisWeek}
                fallback="0"
                to={buildDateRangeUrl(
                  WORK_ORDERS_BASE_ROUTE,
                  startDate,
                  endDate,
                  DASHBOARD_METRIC_FILTERS.completed,
                  "service",
                  selectedDepartmentIds,
                )}
              />
              <div className="mt-auto text-end">
                <Link
                  to={buildComponentShowMoreUrl(servicesElement.href, startDate, endDate, selectedDepartmentIds)}
                  className="btn btn-sm btn-outline-secondary text-capitalize"
                >
                  show more...
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 col-lg-4">
          <div className="card h-100 border-0 shadow-sm">
            <div className="card-body d-flex flex-column gap-2">
              <h6 className="fw-semibold mb-0">{complianceElement.title}</h6>
              <div>
                {loading ? (
                  <LoadingDonut size={180} />
                ) : (
                  <ReactApexChart
                    options={complianceChart.options}
                    series={complianceChart.series}
                    type="pie"
                    height={180}
                  />
                )}
              </div>
              <MetricRow
                label={dueMetricLabel}
                value={complianceData.complianceDueThisWeek}
                fallback="0"
                to={buildDateRangeUrl(
                  complianceElement.href,
                  startDate,
                  endDate,
                  DASHBOARD_METRIC_FILTERS.due_range,
                  "compliance",
                  selectedDepartmentIds,
                )}
              />
              <MetricRow
                label={overdueMetricLabel}
                value={complianceData.complianceOverdue}
                fallback="0"
                to={buildDateRangeUrl(
                  complianceElement.href,
                  startDate,
                  endDate,
                  DASHBOARD_METRIC_FILTERS.overdue,
                  "compliance",
                  selectedDepartmentIds,
                )}
              />
              <MetricRow
                label={completedMetricLabel}
                value={complianceData.complianceCompletedThisWeek}
                fallback="0"
                to={buildDateRangeUrl(
                  WORK_ORDERS_BASE_ROUTE,
                  startDate,
                  endDate,
                  DASHBOARD_METRIC_FILTERS.completed,
                  "compliance",
                  selectedDepartmentIds,
                )}
              />
              <div className="mt-auto text-end">
                <Link
                  to={buildComponentShowMoreUrl(complianceElement.href, startDate, endDate, selectedDepartmentIds)}
                  className="btn btn-sm btn-outline-secondary text-capitalize"
                >
                  show more...
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 col-lg-4">
          <div className="card h-100 border-0 shadow-sm">
            <div className="card-body d-flex flex-column gap-3">
              <h6 className="fw-semibold mb-3">{utilisationElement.title}</h6>
              <MetricRow label="Fleet Availability" value={null} fallback="TBD" />
              <MetricRow label="Fleet Utilisation" value={null} fallback="TBD" />
              <MetricRow label="Fleet Utilisation of Availability" value={null} fallback="TBD" />
              <div className="mt-auto text-end">
                <Link
                  to={utilisationElement.href}
                  className="btn btn-sm btn-outline-secondary text-capitalize"
                >
                  show more...
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 col-lg-4">
          <div className="card h-100 border-0 shadow-sm">
            <div className="card-body d-flex flex-column gap-1">
              <h6 className="fw-semibold mb-3">{assetOverviewElement.title}</h6>
              <MetricRow label="Assets" value={assetOverviewData.assetOverviewAssets} fallback="0" />
              <MetricRow label="Assets on hire" value={assetOverviewData.assetOverviewAssetsOnHire} fallback="0" />
              <div className="mt-auto text-end">
                <Link
                  to={assetOverviewElement.href}
                  className="btn btn-sm btn-outline-secondary text-capitalize"
                >
                  show more...
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperationalDashboardLayer;
